import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  api,
  API_BASE,
  getToken,
  type Annotation,
  type Document,
  type HistoryEntry,
  type Message,
  type Status,
} from "../api";
import { useAuth } from "../auth";
import { useUsers } from "../useUsers";
import { fmtDate, fmtSize, StatusBadge, STATUS_LABEL } from "../ui";
import Avatar from "../components/Avatar";

const TRANSITIONS: Record<Status, Status[]> = {
  draft: ["pending"],
  pending: ["review", "validated"],
  review: ["pending", "draft"],
  validated: ["archived"],
  archived: [],
};

const ADMIN_ONLY = new Set<Status>(["validated", "review", "archived"]);

type Tab = "details" | "annotations" | "messages";

export default function DocumentView() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const users = useUsers();
  const [doc, setDoc] = useState<Document | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [tab, setTab] = useState<Tab>("details");
  const [reminderOpen, setReminderOpen] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<Status | null>(null);

  const reload = useCallback(async () => {
    const r = await api<{ document: Document; history: HistoryEntry[] }>(`/documents/${id}`);
    setDoc(r.document);
    setHistory(r.history);
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  if (!doc || !user) return <p className="muted">Chargement…</p>;

  const isAdmin = user.role === "admin";
  const admins = users.filter((u) => u.role === "admin" && u.id !== user.id);

  // Si je passe en pending et qu'il y a >1 admin candidat, j'ouvre un mini-formulaire pour choisir
  const allowed = TRANSITIONS[doc.status].filter((to) => {
    if (!ADMIN_ONLY.has(to)) return true;
    // Validation/refus : admin assigné OU n'importe quel admin si pas d'assignation
    if (!isAdmin) return false;
    if (doc.assigned_to && doc.assigned_to !== user.id) return false;
    return true;
  });

  async function transition(to: Status, assignedTo?: string) {
    let note: string | null = "";
    if (ADMIN_ONLY.has(to)) {
      const label = STATUS_LABEL[to].toLowerCase();
      note = prompt(`Commentaire pour "${label}" (obligatoire) :`);
      if (note === null || !note.trim()) return;
    }
    await api(`/documents/${id}/transition`, {
      method: "POST",
      json: { to, note: note?.trim() || undefined, assigned_to: assignedTo ?? undefined },
    });
    await reload();
  }

  function handleTransitionClick(to: Status) {
    // Cas spécial : passage en "pending"
    if (to === "pending") {
      // S'il n'y a qu'1 candidat admin (l'auto-assignation va marcher), on déclenche direct.
      // Sinon (≥ 2 candidats), on demande à l'utilisateur de choisir.
      if (admins.length <= 1) {
        transition(to);
      } else {
        setPendingTransition(to);
      }
      return;
    }
    transition(to);
  }

  async function del() {
    if (!confirm("Supprimer définitivement ce document ?")) return;
    await api(`/documents/${id}`, { method: "DELETE" });
    nav("/", { replace: true });
  }

  return (
    <>
      <div className="row spread" style={{ alignItems: "flex-start" }}>
        <h2 style={{ margin: 0, flex: 1 }}>{doc.title}</h2>
        <StatusBadge s={doc.status} />
      </div>
      <div className="row wrap" style={{ gap: 6, margin: "6px 0 14px" }}>
        <span className="entity-chip">{doc.entity_code}</span>
        <span className="tiny">par {doc.created_by_name}</span>
        <span className="tiny">· {fmtDate(doc.created_at)}</span>
        {doc.assigned_to && (() => {
          const a = users.find((u) => u.id === doc.assigned_to);
          return a ? <span className="tiny">· valideur : <b>{a.identifier}</b></span> : null;
        })()}
        {doc.purged_at && (
          <span className="tiny" style={{ color: "var(--terra-deep)" }}>· fichier purgé</span>
        )}
      </div>

      {/* Mini-formulaire d'assignation pour passage en "à valider" (2+ admins) */}
      {pendingTransition === "pending" && (
        <PendingAssignForm
          admins={admins}
          onCancel={() => setPendingTransition(null)}
          onSubmit={(adminId) => { setPendingTransition(null); transition("pending", adminId); }}
        />
      )}

      {/* Actions de circuit */}
      {allowed.length > 0 && (
        <div className="row" style={{ gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          {allowed.map((to) => (
            <button
              key={to}
              className={`btn ${to === "validated" ? "btn-primary" : "btn-secondary"} sm`}
              onClick={() => handleTransitionClick(to)}
            >
              → {STATUS_LABEL[to]}
            </button>
          ))}
        </div>
      )}

      {/* Réassignation par admin */}
      {isAdmin && doc.status === "pending" && (
        <ReassignForm
          docId={doc.id}
          admins={admins}
          currentAssigneeId={doc.assigned_to || null}
          onDone={reload}
        />
      )}

      {/* Rappel */}
      <div style={{ marginBottom: 14 }}>
        {!reminderOpen ? (
          <button className="btn btn-ghost sm" onClick={() => setReminderOpen(true)}>
            🔔 Envoyer un rappel
          </button>
        ) : (
          <Reminder
            docId={doc.id}
            users={users.filter((u) => u.id !== user.id)}
            onClose={() => { setReminderOpen(false); reload(); }}
          />
        )}
      </div>

      <div className="tabs">
        <button className={tab === "details" ? "active" : undefined} onClick={() => setTab("details")}>Détails</button>
        <button className={tab === "annotations" ? "active" : undefined} onClick={() => setTab("annotations")}>Annotations</button>
        <button className={tab === "messages" ? "active" : undefined} onClick={() => setTab("messages")}>Messages</button>
      </div>

      {tab === "details" && <DetailsTab doc={doc} history={history} />}
      {tab === "annotations" && <AnnotationsTab docId={doc.id} meId={user.id} />}
      {tab === "messages" && <MessagesTab docId={doc.id} meId={user.id} />}

      {isAdmin && (
        <>
          <div style={{ height: 30 }} />
          <button
            className="btn btn-ghost full"
            onClick={del}
            style={{ color: "var(--terra-deep)" }}
          >
            Supprimer ce document
          </button>
        </>
      )}
    </>
  );
}

function Reminder({
  docId, users, onClose,
}: {
  docId: string;
  users: { id: string; identifier: string; display_name: string }[];
  onClose: () => void;
}) {
  const [target, setTarget] = useState(users[0]?.id || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    setErr(null);
    if (!target) return setErr("Choisis un destinataire");
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api(`/documents/${docId}/remind`, {
        method: "POST",
        json: { message: msg, target_user_id: target },
      });
      onClose();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="card">
      <div className="section-h" style={{ margin: 0 }}>Envoyer un rappel push</div>
      <label className="section-h" style={{ marginTop: 10 }}>À</label>
      <select value={target} onChange={(e) => setTarget(e.target.value)} required>
        <option value="" disabled>— Choisir —</option>
        {users.map((u) => <option key={u.id} value={u.id}>{u.identifier} — {u.display_name}</option>)}
      </select>
      <label className="section-h" style={{ marginTop: 10 }}>Message</label>
      <textarea
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        rows={2}
        placeholder="ex. Pense à viser ce document, merci"
        autoFocus
      />
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <button className="btn btn-primary sm" onClick={send} disabled={busy || !msg.trim() || !target}>
          Envoyer
        </button>
        <button className="btn btn-ghost sm" onClick={onClose}>Annuler</button>
      </div>
    </div>
  );
}

function PendingAssignForm({
  admins, onSubmit, onCancel,
}: {
  admins: { id: string; identifier: string; display_name: string }[];
  onSubmit: (adminId: string) => void;
  onCancel: () => void;
}) {
  const [target, setTarget] = useState(admins[0]?.id || "");
  return (
    <div className="card" style={{ background: "var(--blue-soft)" }}>
      <div className="section-h" style={{ margin: 0 }}>À quel administrateur l'envoyer ?</div>
      <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ marginTop: 8 }}>
        {admins.map((a) => <option key={a.id} value={a.id}>{a.identifier} — {a.display_name}</option>)}
      </select>
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        <button className="btn btn-primary sm" onClick={() => onSubmit(target)} disabled={!target}>
          Confirmer
        </button>
        <button className="btn btn-ghost sm" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

function ReassignForm({
  docId, admins, currentAssigneeId, onDone,
}: {
  docId: string;
  admins: { id: string; identifier: string; display_name: string }[];
  currentAssigneeId: string | null;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(currentAssigneeId || admins[0]?.id || "");
  if (admins.length === 0) return null;
  if (!open) {
    return (
      <button
        className="btn btn-ghost sm"
        style={{ marginBottom: 8 }}
        onClick={() => setOpen(true)}
      >
        Réassigner à un autre admin
      </button>
    );
  }
  async function save() {
    await api(`/documents/${docId}/assignee`, { method: "PATCH", json: { assigned_to: target } });
    setOpen(false);
    onDone();
  }
  return (
    <div className="card">
      <div className="section-h" style={{ margin: 0 }}>Réassigner</div>
      <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ marginTop: 8 }}>
        {admins.map((a) => <option key={a.id} value={a.id}>{a.identifier} — {a.display_name}</option>)}
      </select>
      <div className="row" style={{ gap: 8, marginTop: 10 }}>
        <button className="btn btn-primary sm" onClick={save}>Enregistrer</button>
        <button className="btn btn-ghost sm" onClick={() => setOpen(false)}>Annuler</button>
      </div>
    </div>
  );
}

function DetailsTab({ doc, history }: { doc: Document; history: HistoryEntry[] }) {
  const fileAvailable = doc.r2_key && !doc.purged_at;
  const fileUrl = fileAvailable ? `${API_BASE}/documents/${doc.id}/file` : null;

  async function openFile() {
    const res = await fetch(fileUrl!, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <>
      {doc.description && (
        <div className="card">
          <div className="section-h" style={{ margin: "0 0 6px" }}>Description</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{doc.description}</div>
        </div>
      )}

      {fileAvailable ? (
        <button className="card tap row spread" onClick={openFile} style={{ width: "100%", textAlign: "left" }}>
          <span>
            <div style={{ fontWeight: 600, color: "var(--navy)" }}>Pièce jointe</div>
            <div className="tiny">{doc.mime_type} · {fmtSize(doc.size_bytes)}</div>
          </span>
          <span style={{ color: "var(--ink-faint)", fontSize: 22 }}>↗</span>
        </button>
      ) : doc.purged_at ? (
        <div className="card" style={{ background: "var(--terra-soft)" }}>
          <div className="muted">Le fichier a été purgé automatiquement (60 jours après validation).</div>
        </div>
      ) : null}

      <div className="section-h">Historique</div>
      <div className="timeline">
        {history.map((h) => (
          <div key={h.id} className="item">
            <div style={{ fontWeight: 600 }}>
              {h.from_status ? `${STATUS_LABEL[h.from_status]} → ` : ""}{STATUS_LABEL[h.to_status]}
            </div>
            <div className="tiny">{h.user_name} · {fmtDate(h.created_at)}</div>
            {h.note && <div className="muted" style={{ marginTop: 4 }}>{h.note}</div>}
          </div>
        ))}
      </div>
    </>
  );
}

function AnnotationsTab({ docId, meId }: { docId: string; meId: string }) {
  const [items, setItems] = useState<Annotation[]>([]);
  const [body, setBody] = useState("");

  const load = useCallback(async () => {
    const r = await api<{ annotations: Annotation[] }>(`/documents/${docId}/annotations`);
    setItems(r.annotations);
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    if (!body.trim()) return;
    await api(`/documents/${docId}/annotations`, { method: "POST", json: { body } });
    setBody("");
    await load();
  }

  async function toggle(a: Annotation) {
    await api(`/annotations/${a.id}`, { method: "PATCH", json: { resolved: !a.resolved } });
    await load();
  }

  return (
    <>
      <div className="thread">
        {items.map((a) => {
          const mine = a.user_id === meId;
          const initials = a.identifier || a.user_name.slice(0, 2).toUpperCase();
          return (
            <div key={a.id} className={`bubble-wrap ${mine ? "me" : ""}`}>
              <Avatar initials={initials} me={mine} title={a.user_name} />
              <div className="bubble">
                <div className="author">{a.user_name}</div>
                <div className="body" style={{ opacity: a.resolved ? 0.55 : 1, textDecoration: a.resolved ? "line-through" : "none" }}>
                  {a.body}
                </div>
                <div className="row spread" style={{ marginTop: 4 }}>
                  <span className="ts">{fmtDate(a.created_at)}{a.page ? ` · p.${a.page}` : ""}</span>
                  <button className="tiny" style={{ color: "var(--blue)" }} onClick={() => toggle(a)}>
                    {a.resolved ? "Rouvrir" : "Résoudre"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="muted">Aucune annotation.</p>}
      </div>

      <div className="composer">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Ajouter une annotation…" />
        <button onClick={submit} disabled={!body.trim()}>Publier</button>
      </div>
    </>
  );
}

function MessagesTab({ docId, meId }: { docId: string; meId: string }) {
  const [items, setItems] = useState<Message[]>([]);
  const [body, setBody] = useState("");

  const load = useCallback(async () => {
    const r = await api<{ messages: Message[] }>(`/documents/${docId}/messages`);
    setItems(r.messages);
  }, [docId]);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    if (!body.trim()) return;
    await api(`/documents/${docId}/messages`, { method: "POST", json: { body } });
    setBody("");
    await load();
  }

  return (
    <>
      <div className="thread">
        {items.map((m) => {
          const mine = m.user_id === meId;
          const initials = m.identifier || m.user_name.slice(0, 2).toUpperCase();
          return (
            <div key={m.id} className={`bubble-wrap ${mine ? "me" : ""}`}>
              <Avatar initials={initials} me={mine} title={m.user_name} />
              <div className="bubble">
                <div className="author">{m.user_name}</div>
                <div className="body">{m.body}</div>
                <div className="ts">{fmtDate(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="muted">Pas encore de message.</p>}
      </div>

      <div className="composer">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Écrire un message…" />
        <button onClick={submit} disabled={!body.trim()}>Envoyer</button>
      </div>
    </>
  );
}
